import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { removeFromSessionStorage, saveToSessionStorage }
  from '@codex/helpers';
import { Checkbox, Tooltip } from '@codex/basics';
import detectVariants from '@dc/fo-experiment/lib/detectVariants.js';
import { EnvironmentThemeProvider } from '@codex/themes';
import { addProduct, removeProduct } from '../../../appState/ducks/comparison';
import {
  toggleComparePopup,
  comparePopupRequestAction
} from '../../../appState/ducks/compareProducts';
import { dispatchClickCompareButton as analyticsDispatchClickCompareButton }
  from '../../../analytics';
import constants from '../constants';

const ADD_PRODUCT = 'ADD_PRODUCT';
const REMOVE_PRODUCT = 'REMOVE_PRODUCT';

class CompareCheckbox extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showTooltip: false
    };

    this.handleClick = this.handleClick.bind(this);
    this.dispatchClickCompareButton = this.dispatchClickCompareButton.bind(this);
    this.checkboxRef = React.createRef();
  }

  componentDidMount() {
    if (this.checkboxRef.current) {
      this.checkboxRef.current.addEventListener('click', this.handleClick);
    }
  }

  handleClick(e) {
    let action = ADD_PRODUCT;
    const { disabled } = this.getCheckboxState();
    const {
      product, products, productSegmentId, containerFormEl
    } = this.props;
    let err = false;
    let status = 'unchecked';
    let errorMessage = '';

    // TODO: check if this functionality is still needed
    // If there is selected at least 1 product to compare and user clicked on label of checkbox,
    // form needs to be submitted and user is redirected to compare page.

    if (products.length > 0 &&
      e.target.matches('.checked [class|="Checkbox__Label"]')
    ) {
      // dispatch analytics also when user clicks on link of checked checkbox
      this.dispatchClickCompareButton();

      const configuration = typeof window !== 'undefined' ? {
        ...window.EXPT_CONFIG,
        QUERY: window.location.search
      } : {};

      if (detectVariants('comparePopup', configuration) === 'A') {
        comparePopupRequestAction(products.map((p) => p.id).join(','));
        this.props.toggleComparePopup(true);
        return;
      }
      if (containerFormEl) {
        containerFormEl.submit();
      }
      return;
    }

    products.forEach((selectedProduct) => {
      if (selectedProduct.id === product.id) {
        // If selectedProduct is already in store, then selectedProduct needs to be removed
        // from store after click and in this case dispatch analytics
        action = REMOVE_PRODUCT;
      } else if (disabled && (products.length >= 4)) {
        // If there are already 4 products in store and user wants to add another one to compare,
        // then tooltip with message is shown to user.
        err = true;
        this.setState({ showTooltip: true }, () =>
          setTimeout(() => {
            this.setState({ showTooltip: false });
          }, 3000));
      }
    });

    if (err === true) {
      errorMessage = 'User Error Message';
      status = 'error';
    } else {
      errorMessage = null;
    }

    if (action === ADD_PRODUCT && products.length < 4) {
      this.props.addProduct({ product, productSegmentId });
      status = 'checked';
    } else if (action === REMOVE_PRODUCT) {
      this.props.removeProduct({ product, productSegmentId });
      status = 'unchecked';
    }

    analyticsDispatchClickCompareButton(product.id, status, errorMessage);

    e.preventDefault();
  }

  getCheckboxLabel(changeLabel) {
    if (changeLabel && this.props.products.length > 0) {
      return `${this.props.translations.checkboxLabel} (${this.props.products.length} of 4)`;
    }
    return this.props.translations.defaultLabel;
  }

  getCheckboxState() {
    // disable all checkboxes which were not selected
    let disabled = false;
    let checked = false;

    const productSelected = this.props.products.find(
      (product) => product.id === this.props.product.id
    );

    if (productSelected) {
      checked = true;
    } else if (this.props.products.length === 4) {
      // Product is not selected and there are already 4 products selected in redux store.
      // Checkbox must be disabled. It's allowed to select max 4 products to compare.
      disabled = true;
    }

    return { checked, disabled };
  }

  dispatchClickCompareButton() {
    const checkedProducts = window.appState.getState().listingPage.comparison.products;
    const fupids = [];
    Object.keys(checkedProducts).forEach((key) => {
      const products = checkedProducts[key];
      products.forEach((product) => {
        fupids.push(product.productID);
      });
    });
    analyticsDispatchClickCompareButton(fupids);

    // saves url pathname of current listing page to session storage
    // which is used in backButton on compare page
    saveToSessionStorage(
      constants.SESSION_STORAGE_COMPARISON_LAST_PAGE_PATH,
      window.location.pathname
    );
    removeFromSessionStorage(
      constants.SESSION_STORAGE_COMPARISON_BACK_LINK_LABEL
    );
  }

  isProductPage() {
    let checkboxPlaceholders = document.querySelectorAll('[data-component="compare-checkbox"]');
    checkboxPlaceholders = Array.from(checkboxPlaceholders);
    return checkboxPlaceholders.length === 1;
  }

  isEmptyCompare() {
    const comparisonItems = window.appState.getState().listingPage.comparison.products;
    return Object.keys(comparisonItems).length === 0 && comparisonItems.constructor === Object;
  }

  render() {
    const { checked, disabled } = this.getCheckboxState();
    const labelText = this.getCheckboxLabel(checked);
    const classList =
      `compare-checkbox${checked ? ' checked' : ''}${disabled ? ' disabled' : ''}`;

    if (this.isProductPage() && this.isEmptyCompare()) {
      return null;
    }

    return (
      <EnvironmentThemeProvider>
        <div className={'checkboxWrapper'}>
          <div className={classList} ref={this.checkboxRef}>
            <Checkbox
              size={'extra-small'}
              disabled={disabled}
              checked={checked}
              centered={this.props.centered}
            >
              {labelText}
            </Checkbox>
          </div>
          <div className={'compare-checkbox-tooltip'}>
            {this.state.showTooltip
              ? (
                <Tooltip
                  arrow={this.props.tooltipArrowDirection}
                  textAlign={'center'}
                  compact
                >
                  {this.props.translations.tooltipText}
                </Tooltip>
              ) : <noscript />}
          </div>
        </div>
      </EnvironmentThemeProvider>
    );
  }
}

CompareCheckbox.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string.isRequired
  }).isRequired,
  productSegmentId: PropTypes.number.isRequired,
  addProduct: PropTypes.func,
  containerFormEl: PropTypes.instanceOf(HTMLFormElement),
  products: PropTypes.arrayOf(PropTypes.object),
  removeProduct: PropTypes.func,
  tooltipArrowDirection: PropTypes.oneOf([
    'top',
    'right',
    'bottom',
    'left'
  ]),
  translations: PropTypes.objectOf(PropTypes.string).isRequired,
  toggleComparePopup: PropTypes.func.isRequired,
  centered: PropTypes.bool
};

CompareCheckbox.defaultProps = {
  addProduct: () => {},
  containerFormEl: null,
  products: [],
  removeProduct: () => {},
  tooltipArrowDirection: 'top',
  centered: true
};

function mapStateToProps(state) {
  const currentPageSegmentId =
    window.digitalData.page.category &&
    window.digitalData.page.category.segmentId;
  return {
    products: state.listingPage.comparison.products[currentPageSegmentId] || [],
    productSegmentId: currentPageSegmentId
  };
}

export default connect(
  mapStateToProps,
  { addProduct, removeProduct, toggleComparePopup }
)(CompareCheckbox);
